import type { PaginationType } from '../types/Pagination.types';
import z, { } from 'zod';
import { CategoryDtoSchema } from '../categories/categories.schema';
import { GameAssetDtoSchema, MediaDtoSchema } from '../media/media.schema';
import { ProviderDtoSchema } from '../providers/providers.schema';
import { PageContentDtoSchema } from '../system-pages';
import { createUpdateZodSchema } from '../types/DateUtils.types';
import { PaginationParamsSchema } from '../types/Pagination.types';

const optionalNumberFromBackendSchema = z.preprocess(
  (value) => {
    if (value === null || value === undefined || value === '') {
      return undefined;
    }

    if (typeof value === 'string') {
      const normalizedValue = Number(value);
      return Number.isNaN(normalizedValue)
        ? undefined
        : normalizedValue;
    }

    if (typeof value === 'number' && Number.isNaN(value)) {
      return undefined;
    }

    return value;
  },
  z.number().optional(),
);

export const PartnerDtoSchema = z.object({
  id: z.string().uuid(),
  partnerId: z.string(),
  partnerCode: z.string(),
  isActive: z.boolean(),
}).merge(createUpdateZodSchema);

export type Partner = z.infer<typeof PartnerDtoSchema>;
export const GameDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string()
    .nullable(),
  gameId: z.string(), // RELAX id, we will not use it

  alias: z.string(), // alias, slug for url
  order: z.number(),

  isActive: z.boolean(),
  isBonusAllowed: z.boolean().optional().default(true),
  cover: MediaDtoSchema.nullable(),
  settings: z.object({
    allow: z.string().optional(),
  }).nullable(),
  categories: z.array(CategoryDtoSchema),
  provider: ProviderDtoSchema.nullish(),
  partner: PartnerDtoSchema.nullish(),
  isFavorite: z.boolean().optional(),
  // field for seo game gallery
  assets: z.array(GameAssetDtoSchema).nullish(),

  pageContent: PageContentDtoSchema.nullish(),

  details: z.object({
    releaseDate: z.string().optional(),
    reels: z.string().optional(),
    rtp: z.string().optional(),
    totalMaxWin: z.string().optional(),
    minBet: optionalNumberFromBackendSchema,
    maxBet: optionalNumberFromBackendSchema,
    volatility: z.string().optional(),
    paylines: z.string().optional(),
    slotype: z.string().optional(),
    maxMultiplier: z.string().optional(),
    // boolean
    multiplayer: z.boolean().optional(),
    freeSpins: z.boolean().optional(),
    bonusRound: z.boolean().optional(),
    wild: z.boolean().optional(),
    autoplay: z.boolean().optional(),
    scatter: z.boolean().optional(),

    // links
    themes: z.string().optional(),
    features: z.string().optional(),
    software: z.string().optional(),
  }).optional().nullable(),

}).merge(createUpdateZodSchema).describe('GameDtoSchema');

export const GameListItemDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  alias: z.string(),
  cover: MediaDtoSchema.nullish().transform(v => v ?? null),
  isFavourite: z.boolean().optional().default(false),
}).describe('GameListItemDtoSchema');

export const GameLaunchUrlPayloadSchema = z.object({
  gameId: z.string(),
  userId: z.string(),
  currency: z.string(),
  language: z.string(),
  country: z.string(),
  device: z.string(),
});

export const GameLaunchUrlResponseSchema = z.object({
  code: z.string(),
  url: z.string(),
});

export const GamesFiltersSchema = z.object({
  name: z.string().optional(),
  gameId: z.string().optional(),
  gameCode: z.string().optional(),
  alias: z.string().optional(),
  isActive: z.boolean().optional(),
  categoryId: z.string().optional(),
  partnerId: z.string().optional(),
  providerIds: z.array(z.string()).optional(),
}).optional();

export const GamesParamsSchema = PaginationParamsSchema(GamesFiltersSchema);

export type GamesFilters = z.infer<typeof GamesFiltersSchema>;
export type GamesFiltersParamsPayload = z.infer<typeof GamesParamsSchema>;

const RoundVerificationSchema = z.object({
  id: z.string().uuid(),
  state: z.enum(['pending', 'success', 'failed', 'processing']),
  transactionId: z.string().nullable(),
  transactionHash: z.string().nullable(),
  proofCount: z.number(),
  completedAt: z.string().nullable(),
  createdAt: z.string(),
});

export const RoundWithWinDtoSchema = z.object({
  id: z.string().uuid(),
  externalRoundId: z.string().nullable(),
  userId: z.string().uuid(),
  partnerId: z.string().uuid().nullable(),
  providerId: z.string().uuid().nullable(),
  gameId: z.string().uuid(),
  sessionId: z.string().uuid().nullable(),
  currency: z.enum(['GC', 'SC']),
  isFinished: z.boolean(),
  totalBet: z.number().nullable(),
  totalWin: z.number().nullable(),
  multiplier: z.number().nullable(),
  winId: z.string().nullable(),
  verificationId: z.string().nullable(),
  verification: RoundVerificationSchema.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const ShareTokenDtoSchema = z.object({
  shareToken: z.string(),
  shareUrl: z.string(),
});

const SharedGameSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  alias: z.string(),
  cover: MediaDtoSchema.nullable(),
});

export const SharedRoundDtoSchema = z.object({
  game: SharedGameSummarySchema,
  currency: z.enum(['GC', 'SC']),
  totalBet: z.number().nullable(),
  totalWin: z.number().nullable(),
  multiplier: z.number().nullable(),
  winId: z.string().nullable().optional(),
  verification: RoundVerificationSchema,
  createdAt: z.string(),
});

export const RoundsWithWinsParamsSchema = PaginationParamsSchema();

export const GamesWithWinsFiltersSchema = z.object({
  name: z.string().optional(),
}).optional();

export const GamesWithWinsParamsSchema = PaginationParamsSchema(GamesWithWinsFiltersSchema);
export type GamesWithWinsParams = z.infer<typeof GamesWithWinsParamsSchema>;

export type ShareTokenDto = z.infer<typeof ShareTokenDtoSchema>;
export type SharedRoundDto = z.infer<typeof SharedRoundDtoSchema>;
export type RoundWithWinDto = z.infer<typeof RoundWithWinDtoSchema>;
export type RoundsWithWinsParams = z.infer<typeof RoundsWithWinsParamsSchema>;

export type GameDto = z.infer<typeof GameDtoSchema>;
export type GameListItemDto = z.infer<typeof GameListItemDtoSchema>;
export type GameListDto = PaginationType<GameListItemDto>;
export type GameLaunchUrlPayload = z.infer<typeof GameLaunchUrlPayloadSchema>;
export type GameLaunchUrlResponse = z.infer<typeof GameLaunchUrlResponseSchema>;
